import * as React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  BulkUpdateTask,
  FieldChangeReasonModal,
} from '@/components/tasks/FieldChangeReasonModal';
import {
  ChangeField,
  TaskFieldChangeReason,
  TreatmentCheckInCheckbox,
} from '@/constants/task-field-change-reasons';

/** Request object for prompting user for a change reason (single task) */
interface ChangeReasonRequest {
  fieldType: ChangeField;
  oldValue: string | null;
  newValue: string;
  taskName: string;
  /** Optional list of other tasks that can also be updated */
  otherTasks?: BulkUpdateTask[];
  /** Optional recent task update text to suggest as pre-fill for the reason */
  recentUpdate?: string;
  /** ISO timestamp of the recent update (for freshness check in modal) */
  recentUpdateCreatedAt?: string;
  /** Optional warning message displayed as an amber banner */
  warningMessage?: string;
  /** Optional required checkboxes that must all be checked before confirming */
  requiredCheckboxes?: readonly TreatmentCheckInCheckbox[];
}

/** Request object for bulk change reason */
interface BulkChangeReasonRequest {
  fieldType: ChangeField;
  newValue: string;
  taskCount: number;
  /** Optional warning message displayed as an amber banner */
  warningMessage?: string;
  /** Optional required checkboxes that must all be checked before confirming */
  requiredCheckboxes?: readonly TreatmentCheckInCheckbox[];
}

/** Selected task info for bulk update */
export interface SelectedTaskInfo {
  id: string;
  name: string;
}

/** Result returned when user confirms the modal */
export interface ChangeReasonResult {
  reason: TaskFieldChangeReason;
  reasonDetail?: string;
  /** Task IDs selected for bulk update */
  selectedTaskIds?: string[];
  /** Full task info for selected tasks (for push changes) */
  selectedTasks?: SelectedTaskInfo[];
}

/** Internal state for modal request */
interface ModalRequest {
  mode: 'single' | 'bulk';
  fieldType: ChangeField;
  oldValue: string | null;
  newValue: string;
  taskName: string;
  taskCount?: number;
  otherTasks?: BulkUpdateTask[];
  /** Recent task update text to suggest in the modal */
  recentUpdate?: string;
  /** ISO timestamp of the recent update (for freshness check in modal) */
  recentUpdateCreatedAt?: string;
  warningMessage?: string;
  requiredCheckboxes?: readonly TreatmentCheckInCheckbox[];
}

interface FieldChangeReasonContextValue {
  /**
   * Prompt the user for a change reason (single task).
   * Returns the reason data if confirmed, or null if cancelled.
   */
  requestChangeReason: (
    request: ChangeReasonRequest,
  ) => Promise<ChangeReasonResult | null>;
  /**
   * Prompt the user for a change reason (bulk operation).
   * Returns the reason data if confirmed, or null if cancelled.
   */
  requestBulkChangeReason: (
    request: BulkChangeReasonRequest,
  ) => Promise<ChangeReasonResult | null>;
  /**
   * Whether a change reason modal is currently open or pending.
   * Use this to prevent parent dialogs from closing during the async flow.
   */
  isPending: boolean;
}

const FieldChangeReasonContext =
  createContext<FieldChangeReasonContextValue | null>(null);

interface FieldChangeReasonProviderProps {
  children: React.ReactNode;
}

/**
 * Provider that enables any component to request a field change reason from the user.
 * Renders a single modal instance and manages Promise resolution for each request.
 * Supports both single-task and bulk operation modes.
 */
export const FieldChangeReasonProvider: React.FC<
  FieldChangeReasonProviderProps
> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<ModalRequest | null>(
    null,
  );

  // Store resolve/reject functions for the current request
  const promiseRef = useRef<{
    resolve: (result: ChangeReasonResult | null) => void;
  } | null>(null);

  const requestChangeReason = useCallback(
    (request: ChangeReasonRequest): Promise<ChangeReasonResult | null> => {
      return new Promise((resolve) => {
        promiseRef.current = { resolve };
        setCurrentRequest({
          mode: 'single',
          fieldType: request.fieldType,
          oldValue: request.oldValue,
          newValue: request.newValue,
          taskName: request.taskName,
          otherTasks: request.otherTasks,
          recentUpdate: request.recentUpdate,
          recentUpdateCreatedAt: request.recentUpdateCreatedAt,
          warningMessage: request.warningMessage,
          requiredCheckboxes: request.requiredCheckboxes,
        });
        setIsOpen(true);
      });
    },
    [],
  );

  const requestBulkChangeReason = useCallback(
    (request: BulkChangeReasonRequest): Promise<ChangeReasonResult | null> => {
      return new Promise((resolve) => {
        promiseRef.current = { resolve };
        setCurrentRequest({
          mode: 'bulk',
          fieldType: request.fieldType,
          oldValue: null,
          newValue: request.newValue,
          taskName: `${request.taskCount} tasks`,
          taskCount: request.taskCount,
          warningMessage: request.warningMessage,
          requiredCheckboxes: request.requiredCheckboxes,
        });
        setIsOpen(true);
      });
    },
    [],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    promiseRef.current?.resolve(null);
    promiseRef.current = null;
    setCurrentRequest(null);
  }, []);

  const handleConfirm = useCallback(
    async (data: ChangeReasonResult): Promise<void> => {
      setIsOpen(false);
      promiseRef.current?.resolve(data);
      promiseRef.current = null;
      setCurrentRequest(null);
    },
    [],
  );

  const value = useMemo(
    () => ({ requestChangeReason, requestBulkChangeReason, isPending: isOpen }),
    [requestChangeReason, requestBulkChangeReason, isOpen],
  );

  return (
    <FieldChangeReasonContext.Provider value={value}>
      {children}
      {currentRequest && (
        <FieldChangeReasonModal
          isOpen={isOpen}
          onClose={handleClose}
          onConfirm={handleConfirm}
          fieldType={currentRequest.fieldType}
          oldValue={currentRequest.oldValue}
          newValue={currentRequest.newValue}
          taskName={currentRequest.taskName}
          otherTasks={currentRequest.otherTasks}
          mode={currentRequest.mode}
          taskCount={currentRequest.taskCount}
          recentUpdate={currentRequest.recentUpdate}
          recentUpdateCreatedAt={currentRequest.recentUpdateCreatedAt}
          warningMessage={currentRequest.warningMessage}
          requiredCheckboxes={currentRequest.requiredCheckboxes}
        />
      )}
    </FieldChangeReasonContext.Provider>
  );
};

/**
 * Hook to request field change reasons from the user.
 * Must be used within a FieldChangeReasonProvider.
 */
export const useFieldChangeReason = (): FieldChangeReasonContextValue => {
  const context = useContext(FieldChangeReasonContext);
  if (!context) {
    throw new Error(
      'useFieldChangeReason must be used within a FieldChangeReasonProvider',
    );
  }
  return context;
};
